// src/components/CombatView.js
import React, { useState, useEffect, useCallback, useContext } from 'react';
import styles from './CombatView.module.css';
import { MonsterData } from '../data/monsterData';
import { ItemData } from '../data/ItemData';
import { getItemImageSrc } from '../utils/getItemImageSrc';
import { MessageLogContext } from '../contexts/MessageLogContext';

const CombatView = ({
    player,
    initialMonsters,
    onCombatEnd,
    isBossWaveFight,
    isFinalBossFight,
    currentWave,
    totalWaves
}) => {
  const { addMessage: logCombatMessage } = useContext(MessageLogContext);
  const [playerStats, setPlayerStats] = useState({ ...player.stats });
  const [monsters, setMonsters] = useState([]);
  const [playerTurn, setPlayerTurn] = useState(true);
  const [selectedMonsterId, setSelectedMonsterId] = useState(null);
  const [combatLog, setCombatLog] = useState([]);
  const [showLootSummary, setShowLootSummary] = useState(false);
  const [finalLoot, setFinalLoot] = useState([]);
  const [finalXp, setFinalXp] = useState([]);

  useEffect(() => {
    const monsterInstances = initialMonsters.map(m => {
        const base = MonsterData[m.key];
        if (!base) {
            console.error(`CombatView: Monster data not found for key ${m.key}`);
            logCombatMessage(`Error: Could not load monster ${m.key}.`, 'error');
            return null;
        }
        return {
            ...m, // Includes key, instanceId
            name: base.name,
            stats: { ...base.stats },
            maxHealth: base.stats.health,
            lootTable: base.lootTable,
            img: base.img,
            isBoss: base.isBoss || false,
        };
    }).filter(m => m !== null);

    setMonsters(monsterInstances);
    if (monsterInstances.length > 0) {
        setSelectedMonsterId(monsterInstances[0].instanceId);
    }
    setPlayerStats({ ...player.stats });
    setPlayerTurn(true);
    setCombatLog([]);
    setShowLootSummary(false);
    setFinalLoot([]);
    setFinalXp([]);
  }, [initialMonsters, player.stats, logCombatMessage]);

  const addToCombatLog = useCallback((message, type = 'info') => {
    setCombatLog(prevLog => [...prevLog.slice(-9), { message, type, id: `${Date.now()}-${Math.random()}` }]);
    logCombatMessage(message, type);
  }, [logCombatMessage]);

  const handlePlayerAttack = useCallback(() => {
    if (!playerTurn || !selectedMonsterId || monsters.length === 0) return;

    const targetMonsterIndex = monsters.findIndex(m => m.instanceId === selectedMonsterId);
    if (targetMonsterIndex === -1) {
        addToCombatLog("Selected monster not found!", "error");
        return;
    }

    let currentMonster = { ...monsters[targetMonsterIndex] };
    const playerDamageRoll = Math.floor(Math.random() * (playerStats.damageMax - playerStats.damageMin + 1)) + playerStats.damageMin;
    addToCombatLog(`${player.name} attacks ${currentMonster.name} for ${playerDamageRoll} damage!`, 'player_attack');
    currentMonster.stats.health -= playerDamageRoll;

    let updatedMonstersAfterAttack = [...monsters];

    if (currentMonster.stats.health <= 0) {
        addToCombatLog(`${currentMonster.name} defeated!`, 'victory');
        updatedMonstersAfterAttack.splice(targetMonsterIndex, 1);

        const monsterLoot = {};
        if (currentMonster.lootTable) {
            for (const [itemKey, lootDetails] of Object.entries(currentMonster.lootTable)) {
                if (Math.random() < lootDetails.chance) {
                    const quantity = Array.isArray(lootDetails.quantity)
                        ? Math.floor(Math.random() * (lootDetails.quantity[1] - lootDetails.quantity[0] + 1)) + lootDetails.quantity[0]
                        : lootDetails.quantity;
                    monsterLoot[itemKey] = (monsterLoot[itemKey] || 0) + quantity;
                }
            }
        }
        setFinalLoot(prev => [...prev, monsterLoot]);
        setFinalXp(prev => [...prev, currentMonster.stats.xp || 0]);
        
        setMonsters(updatedMonstersAfterAttack); // Update state *before* setShowLootSummary

        if (updatedMonstersAfterAttack.length === 0) {
            setShowLootSummary(true); 
            return; 
        } else {
            setSelectedMonsterId(updatedMonstersAfterAttack[0].instanceId); 
            setPlayerTurn(false);
        }
    } else {
        updatedMonstersAfterAttack[targetMonsterIndex] = currentMonster;
        setMonsters(updatedMonstersAfterAttack);
        setPlayerTurn(false); 
    }
  }, [playerTurn, selectedMonsterId, monsters, playerStats, player.name, addToCombatLog]);

  useEffect(() => {
    if (!playerTurn && monsters.length > 0 && !showLootSummary && playerStats.health.current > 0) {
      const monsterAttackDelay = setTimeout(() => {
        const messagesForThisTurn = [];
        let currentTurnPlayerHealth = playerStats.health.current;
        let playerDefeatedThisTurn = false;

        monsters.forEach(monster => {
          if (monster.stats.health > 0 && currentTurnPlayerHealth > 0) {
            const monsterDamageRoll = Math.floor(Math.random() * (monster.stats.maxDamage - monster.stats.minDamage + 1)) + monster.stats.minDamage;
            messagesForThisTurn.push({ text: `${monster.name} attacks ${player.name} for ${monsterDamageRoll} damage!`, type: 'monster_attack' });
            currentTurnPlayerHealth = Math.max(0, currentTurnPlayerHealth - monsterDamageRoll);
            if (currentTurnPlayerHealth <= 0 && !playerDefeatedThisTurn) {
              playerDefeatedThisTurn = true;
              messagesForThisTurn.push({ text: `${player.name} has been defeated!`, type: 'defeat' });
            }
          }
        });

        // Update player stats first
        setPlayerStats(prevStats => ({
          ...prevStats,
          health: { ...prevStats.health, current: currentTurnPlayerHealth }
        }));

        // Then log messages
        messagesForThisTurn.forEach(msg => addToCombatLog(msg.text, msg.type));

        // Then handle turn change or loot summary
        if (playerDefeatedThisTurn) {
          setShowLootSummary(true);
        } else {
          setPlayerTurn(true);
        }
      }, 1000);

      return () => clearTimeout(monsterAttackDelay);
    }
  }, [playerTurn, monsters, player.name, addToCombatLog, showLootSummary, playerStats.health]);


  const handleFlee = () => {
    addToCombatLog("Player attempts to flee...", "info");
    onCombatEnd({ outcome: 'fled', finalPlayerHealth: playerStats.health.current, lootArray: [], xpArray: [] });
  };

  const handleEndCombatAfterSummary = () => {
    const currentFightOutcome = playerStats.health.current <= 0 ? 'defeat' : (monsters.length === 0 ? 'victory' : 'unknown_interim_state');
    onCombatEnd({
        outcome: currentFightOutcome,
        finalPlayerHealth: playerStats.health.current,
        lootArray: finalLoot,
        xpArray: finalXp
    });
  };

  if (showLootSummary) {
    const allLootItems = [];
    if (finalLoot) {
      finalLoot.forEach((lootSet, lootSetIndex) => {
        if (lootSet && typeof lootSet === 'object') {
          Object.entries(lootSet).forEach(([itemKey, quantity]) => {
            allLootItems.push({
              id: `${itemKey}-${lootSetIndex}-${quantity}-${Math.random().toString(36).substr(2, 9)}`,
              name: ItemData[itemKey]?.name || itemKey,
              quantity: quantity
            });
          });
        }
      });
    }
    return (
        <div className={styles.combatOverlay}>
            <div className={styles.combatModal}>
                <h3>{playerStats.health.current <= 0 ? "Defeated!" : (monsters.length === 0 ? "Victory!" : "Combat Ended")}</h3>
                {playerStats.health.current > 0 && monsters.length === 0 && (
                    <>
                        <h4>Loot Obtained:</h4>
                        {allLootItems.length > 0 ? (
                            <ul className={styles.lootList}>
                                {allLootItems.map(loot => (
                                    <li key={loot.id}>
                                        {loot.name}: {loot.quantity}
                                    </li>
                                ))}
                            </ul>
                        ) : <p>No loot this time.</p>}
                        <h4>XP Gained:</h4>
                        <p>{finalXp.reduce((acc, curr) => acc + curr, 0)} XP</p>
                    </>
                )}
                 {playerStats.health.current <= 0 && <p>You have fallen in battle.</p>}
                <button onClick={handleEndCombatAfterSummary} className={styles.combatButton}>
                    {isBossWaveFight && monsters.length === 0 && playerStats.health.current > 0 ? "Next Wave" : "Continue"}
                </button>
            </div>
        </div>
    );
  }
  let waveMessage = "";
  if (isBossWaveFight) { 
    waveMessage = `Wave ${currentWave} of ${totalWaves}`;
  } else if (isFinalBossFight) { 
    waveMessage = "Final Boss!";
  }
  return (
    <div className={styles.combatOverlay}>
      <div className={styles.combatModal}>
        {waveMessage && <h3 className={styles.waveInfo}>{waveMessage}</h3>}
        <div className={styles.combatantsArea}>
          <div className={styles.playerArea}>
            <h4>{player.name}</h4>
            <img src={player.image || getItemImageSrc('player/player_default_combat.png')} alt={player.name} className={styles.characterImage} />
            <div className={styles.healthBarContainer}>
              <div
                className={styles.healthBar}
                style={{ width: `${(playerStats.health.current / playerStats.health.max) * 100}%` }}
              />
            </div>
            <p>HP: {playerStats.health.current} / {playerStats.health.max}</p>
          </div>
          <div className={styles.monstersArea}>
            {monsters.map((monster) => (
              <div
                key={monster.instanceId}
                className={`${styles.monsterCard} ${selectedMonsterId === monster.instanceId ? styles.selectedMonster : ''}`}
                onClick={() => setSelectedMonsterId(monster.instanceId)}
              >
                <h5>{monster.name} {monster.isBoss ? '(Boss)' : ''}</h5>
                <img src={getItemImageSrc(monster.img)} alt={monster.name} className={styles.characterImage} />
                <div className={styles.healthBarContainer}>
                  <div
                    className={styles.healthBar}
                    style={{ width: `${(monster.stats.health / monster.maxHealth) * 100}%` }}
                  />
                </div>
                <p>HP: {monster.stats.health > 0 ? monster.stats.health : 0} / {monster.maxHealth}</p>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.combatLogArea}>
          <h4>Combat Log</h4>
          <div className={styles.logBox}>
            {combatLog.map(entry => (
              <p key={entry.id} className={styles[entry.type] || styles.logInfo}>{entry.message}</p>
            ))}
          </div>
        </div>
        <div className={styles.combatActions}>
          <button
            onClick={handlePlayerAttack}
            disabled={!playerTurn || !selectedMonsterId || monsters.length === 0 || playerStats.health.current <= 0}
            className={styles.combatButton}
          >
            Attack
          </button>
          <button
            onClick={handleFlee}
            disabled={playerStats.health.current <= 0 || (isBossWaveFight || isFinalBossFight)}
            className={`${styles.combatButton} ${styles.fleeButton}`}
          >
            Flee
          </button>
        </div>
      </div>
    </div>
  );
};
export default CombatView;